/* Env import sheet: pure model. The target label, the request body, the preview verdict and the toast text. */
import type { App, EnvVisibility } from '@hangar/shared';
import type { EnvImportInput } from '@/api/endpoints';
import type { EnvImportResult } from '@/api/schemas';
import { needsApp, needsEnvironment, needsPlatform } from './envHelpers';
import type { ScopeSelection } from './ScopePicker';

export type ImportConflict = 'skip' | 'new_version' | 'fail';

export const ACTION_TONE: Record<string, string> = { create: 'text-succeeded', new_version: 'text-running', unchanged: 'text-subtle', skip: 'text-subtle', error: 'text-failed' };

export interface ImportDraft {
  content: string;
  defaultVisibility: EnvVisibility;
  onConflict: ImportConflict;
  note: string;
}

/** "project · app-slug · staging · android": what the document is imported into. */
export function importTargetLabel(scope: ScopeSelection, app: App | undefined): string {
  return [scope.scope === 'org' ? 'organization' : 'project', needsApp(scope.scope) ? app?.slug : undefined, needsEnvironment(scope.scope) ? scope.environment : undefined, needsPlatform(scope.scope) ? scope.platform : undefined].filter(Boolean).join(' · ');
}

/** The scope part of the request: organization-scoped imports carry no project; each narrowing field only when the scope needs it and it is set. */
function scopeFields(scope: ScopeSelection, projectId: string, app: App | undefined): Partial<EnvImportInput> {
  return {
    ...(scope.scope === 'org' ? {} : { projectId }),
    ...(needsApp(scope.scope) && app ? { appId: app.id } : {}),
    ...(needsEnvironment(scope.scope) && scope.environment ? { environment: scope.environment } : {}),
    ...(needsPlatform(scope.scope) && scope.platform ? { platform: scope.platform } : {}),
  };
}

export function importRequestBody(i: { scope: ScopeSelection; projectId: string; app: App | undefined; draft: ImportDraft; dryRun: boolean }): EnvImportInput {
  const { draft } = i;
  return {
    ...scopeFields(i.scope, i.projectId, i.app),
    content: draft.content,
    defaultVisibility: draft.defaultVisibility,
    onConflict: draft.onConflict,
    dryRun: i.dryRun,
    ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
  };
}

/** The import can only be applied after a preview without error items. */
export function importSubmitState(preview: EnvImportResult | null): { disabled: boolean; title: string | undefined } {
  if (!preview) return { disabled: true, title: 'Preview first' };
  const blocking = preview.items.some((i) => i.action === 'error');
  return { disabled: blocking, title: blocking ? 'Fix the items marked as error' : undefined };
}

export function importSummaryText(s: EnvImportResult['summary']): string {
  return `${s.created} created · ${s.newVersions} new versions · ${s.unchanged} unchanged · ${s.errors} errors`;
}
